import React, { useState } from 'react';
import { Add, Delete, Close, NotificationsActive, LocalOffer } from '@mui/icons-material';
import { Box, Chip, Divider, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import ExchangeItemDiscountPopover from '../../components/pos/discount/ExchangeItemDiscountPopover';
import { computeDiscountAmount } from '../../utils/exchangeDiscountMath';
import ItemBarcode from '../../components/pos/ItemBarcode';

export default function DashboardExchangeWorkflowPanel({
  exchangeSourceOrder,
  exchangeReturnItems,
  setExchangeReturnItems,
  reasonCodes,
  exchangeReturnTotal,
  exchangeNewItems,
  setExchangeNewItems,
  nearMissHints = [],
  appliedPromotions = [],
  scrollbarStyle,
  handleCancelExchange,
  isIncentiveProduct,
}) {
  const [nearMissDismissed, setNearMissDismissed] = useState(false);
  // A newly-qualifying (or newly-lost) promotion should re-surface the hint
  // even if an earlier one was dismissed — key the dismissal off which
  // promotions are currently near-miss, not just a one-time flag. Adjusted
  // during render (not an effect) per React's "adjusting state when a prop
  // changes" pattern, so this needs no extra render/commit cycle.
  const [lastNearMissKey, setLastNearMissKey] = useState('');
  const nearMissKey = nearMissHints.map((h) => h.promotion_id).join(',');
  if (nearMissKey !== lastNearMissKey) {
    setLastNearMissKey(nearMissKey);
    setNearMissDismissed(false);
  }
  const orderItems = exchangeSourceOrder?.items || [];
  const fmtAmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

  const [discountAnchorEl, setDiscountAnchorEl] = useState(null);
  const [discountTargetItem, setDiscountTargetItem] = useState(null);

  const openDiscountPopover = (e, item) => {
    setDiscountAnchorEl(e.currentTarget);
    setDiscountTargetItem(item);
  };
  const closeDiscountPopover = () => {
    setDiscountAnchorEl(null);
    setDiscountTargetItem(null);
  };

  const handleApplyDiscount = ({ discount, modifiedValue }) => {
    if (!discountTargetItem) return;
    const productId = discountTargetItem.product_id;
    setExchangeNewItems((prev) => prev.map((i) => {
      if (i.product_id !== productId) return i;
      return {
        ...i,
        discount_id: discount.id,
        discount_name: discount.name,
        discount_type: discount.discount_type,
        discount_value: discount.discount_value,
        modified_value: modifiedValue,
        discount_amount: computeDiscountAmount(discount.discount_type, discount.discount_value, modifiedValue, i.retail_price, i.quantity),
      };
    }));
    closeDiscountPopover();
  };

  const handleRemoveDiscount = () => {
    if (!discountTargetItem) return;
    const productId = discountTargetItem.product_id;
    setExchangeNewItems((prev) => prev.map((i) => {
      if (i.product_id !== productId) return i;
      return {
        product_id: i.product_id,
        product_name: i.product_name,
        sku: i.sku,
        display_barcode: i.display_barcode,
        retail_price: i.retail_price,
        quantity: i.quantity,
      };
    }));
    closeDiscountPopover();
  };
  const getExchangeUnitPrice = (item) => {
    const quantity = parseFloat(item?.quantity || 0);
    const lineTotal = parseFloat(item?.line_total || 0);

    if (quantity > 0 && Number.isFinite(lineTotal)) {
      return lineTotal / quantity;
    }

    return parseFloat(item?.unit_price || 0);
  };
  const getExchangeDiscountSummary = (item) => {
    const derivedTotal = [
      item?.discount_amount,
      item?.bill_discount_amount,
      item?.promotion_discount_amount,
      item?.bill_promotion_discount_amount,
    ].reduce((sum, value) => sum + (parseFloat(value || 0) || 0), 0);

    const totalDiscount = parseFloat(item?.total_discount_amount || 0) || derivedTotal;
    if (totalDiscount <= 0) return null;

    const labels = [
      item?.discount_name,
      item?.promotion_name,
      parseFloat(item?.bill_discount_amount || 0) > 0 ? (item?.bill_discount_name || 'Bill Discount') : null,
      parseFloat(item?.bill_promotion_discount_amount || 0) > 0 ? (item?.bill_promotion_name || 'Bill Promo') : null,
    ].filter(Boolean);

    return {
      label: labels.length > 0 ? labels.join(' · ') : 'Discount Applied',
      total: totalDiscount,
    };
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', ...scrollbarStyle }}>
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, bgcolor: '#F5F3FF' }}>
        <Typography fontSize="0.72rem" fontWeight={700} color="#6366F1" sx={{ cursor: 'pointer', flexShrink: 0, '&:hover': { textDecoration: 'underline' } }} onClick={handleCancelExchange}>← Back</Typography>
        <Typography fontSize="0.75rem" fontWeight={800} color="#1E293B" noWrap>
          Exchange: {exchangeSourceOrder?.order_number}
        </Typography>
        {exchangeSourceOrder?.customer_name && (
          <Typography fontSize="0.68rem" color="#94A3B8" noWrap sx={{ ml: 'auto' }}>
            {exchangeSourceOrder.customer_name}
          </Typography>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, ...scrollbarStyle }}>
        <Typography fontSize="0.62rem" fontWeight={900} color="#C2410C" letterSpacing="0.06rem" sx={{ mb: 1 }}>
          RETURNING (tap to select)
        </Typography>

        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          {orderItems.map((item) => {
            const returnable = parseFloat(item.quantity_returnable ?? item.quantity ?? 0);
            const fullyReturned = returnable === 0;
            const sel = exchangeReturnItems[item.id];
            const isSelected = sel?.selected && !fullyReturned;
            const exchangeUnitPrice = getExchangeUnitPrice(item);
            const discountSummary = getExchangeDiscountSummary(item);

            return (
              <Box
                key={item.id}
                onClick={fullyReturned ? undefined : () => {
                  setExchangeReturnItems((prev) => ({
                    ...prev,
                    [item.id]: prev[item.id]?.selected
                      ? { ...prev[item.id], selected: false }
                      : { selected: true, quantity: 1, reason_code_id: '' },
                  }));
                }}
                sx={{
                  border: '1px solid',
                  borderRadius: '10px',
                  p: 1.25,
                  borderColor: isSelected ? '#6366F1' : '#E2E8F0',
                  borderLeft: isSelected ? '3px solid #6366F1' : '1px solid #E2E8F0',
                  bgcolor: fullyReturned ? '#F8FAFC' : isSelected ? '#F5F3FF' : '#fff',
                  cursor: fullyReturned ? 'default' : 'pointer',
                  opacity: fullyReturned ? 0.5 : 1,
                  transition: 'all 0.12s',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontSize="0.8rem" fontWeight={700} color="#1E293B" noWrap>{item.product_name}</Typography>
                    <Typography fontSize="0.65rem" color="#94A3B8">
                      {item.product_sku} · {fmtAmt(exchangeUnitPrice)} each · Returnable: {returnable}
                    </Typography>
                    <ItemBarcode code={item.display_barcode} />
                    {discountSummary && (
                      <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" sx={{ mt: 0.6, rowGap: 0.5 }}>
                        <Chip
                          label="Discount Applied"
                          size="small"
                          sx={{ fontSize: '0.58rem', height: 18, bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 700 }}
                        />
                        <Typography fontSize="0.62rem" color="#16A34A" fontWeight={600}>
                          {discountSummary.label} · Saved {fmtAmt(discountSummary.total)}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                  {fullyReturned
                    ? <Typography fontSize="0.62rem" color="#DC2626" fontWeight={700}>Fully returned</Typography>
                    : isSelected
                      ? <Typography fontSize="0.68rem" color="#6366F1" fontWeight={700}>Selected</Typography>
                      : <Typography fontSize="0.68rem" color="#94A3B8">Tap to include</Typography>}
                </Box>

                {isSelected && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #EDE9FE' }} onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography fontSize="0.7rem" color="#64748B">Qty:</Typography>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
                          <IconButton
                            size="small"
                            sx={{ borderRadius: 0, width: 24, height: 24 }}
                            disabled={sel.quantity <= 1}
                            onClick={() => setExchangeReturnItems((prev) => ({ ...prev, [item.id]: { ...prev[item.id], quantity: Math.max(1, prev[item.id].quantity - 1) } }))}
                          >
                            <Typography fontSize="0.9rem" lineHeight={1}>−</Typography>
                          </IconButton>
                          <Typography fontSize="0.78rem" fontWeight={700} sx={{ px: 1 }}>{sel.quantity}</Typography>
                          <IconButton
                            size="small"
                            sx={{ borderRadius: 0, width: 24, height: 24 }}
                            disabled={sel.quantity >= returnable}
                            onClick={() => setExchangeReturnItems((prev) => ({ ...prev, [item.id]: { ...prev[item.id], quantity: Math.min(returnable, prev[item.id].quantity + 1) } }))}
                          >
                            <Add sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                        <Typography fontSize="0.7rem" color="#94A3B8">/ {returnable}</Typography>
                      </Box>
                      <Typography fontSize="0.78rem" fontWeight={700} color="#C2410C" sx={{ ml: 'auto' }}>
                        {fmtAmt(exchangeUnitPrice * sel.quantity)}
                      </Typography>
                    </Box>
                    {reasonCodes.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Select
                          size="small"
                          fullWidth
                          displayEmpty
                          value={sel.reason_code_id || ''}
                          onChange={(e) => setExchangeReturnItems((prev) => ({
                            ...prev,
                            [item.id]: { ...prev[item.id], reason_code_id: e.target.value },
                          }))}
                          sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.75 } }}
                        >
                          <MenuItem value="" disabled><em>Select reason…</em></MenuItem>
                          {reasonCodes.map((rc) => (
                            <MenuItem key={rc.id} value={rc.id} sx={{ fontSize: '0.75rem' }}>{rc.name || rc.code}</MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>

        {exchangeReturnTotal > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#FFF7ED', borderRadius: '8px', px: 1.5, py: 0.75, mb: 2 }}>
            <Typography fontSize="0.78rem" color="#92400E">Return Credit</Typography>
            <Typography fontSize="0.85rem" fontWeight={800} color="#C2410C">−{fmtAmt(exchangeReturnTotal)}</Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography fontSize="0.62rem" fontWeight={900} color="#4F46E5" letterSpacing="0.06rem">
            NEW ITEMS
          </Typography>
          <Typography fontSize="0.65rem" color="#94A3B8">Search or scan barcode above</Typography>
        </Box>

        {exchangeNewItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, border: '2px dashed #E2E8F0', borderRadius: '10px' }}>
            <Typography fontSize="0.78rem" color="#94A3B8">No items added yet</Typography>
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {exchangeNewItems.map((item) => {
              const lineSubtotal = item.retail_price * item.quantity;
              const hasDiscount = !!item.discount_id && item.discount_amount > 0;

              return (
                <Box key={item.product_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '10px', border: '1px solid #C7D2FE', bgcolor: '#EEF2FF' }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontSize="0.8rem" fontWeight={700} color="#1E293B" noWrap>{item.product_name}</Typography>
                    <Typography fontSize="0.65rem" color="#94A3B8">{fmtAmt(item.retail_price)} each</Typography>
                    <ItemBarcode code={item.display_barcode} />
                    {hasDiscount ? (
                      <Chip
                        label={`🏷 ${item.discount_name}`}
                        size="small"
                        onClick={(e) => openDiscountPopover(e, item)}
                        sx={{ mt: 0.4, fontSize: '0.58rem', height: 18, bgcolor: '#F0FDF4', color: '#16A34A', fontWeight: 700, cursor: 'pointer' }}
                      />
                    ) : (
                      <Typography
                        onClick={(e) => openDiscountPopover(e, item)}
                        sx={{ mt: 0.3, fontSize: '0.65rem', color: '#4F46E5', fontWeight: 700, cursor: 'pointer', width: 'fit-content', '&:hover': { textDecoration: 'underline' } }}
                      >
                        + Add discount
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #C7D2FE', borderRadius: '8px', overflow: 'hidden' }}>
                    <IconButton
                      size="small"
                      sx={{ borderRadius: 0, width: 24, height: 24, color: '#6366F1' }}
                      onClick={() => setExchangeNewItems((prev) => prev.map((i) => {
                        if (i.product_id !== item.product_id) return i;
                        const quantity = Math.max(1, i.quantity - 1);
                        const discount_amount = i.discount_id
                          ? computeDiscountAmount(i.discount_type, i.discount_value, i.modified_value, i.retail_price, quantity)
                          : i.discount_amount;
                        return { ...i, quantity, discount_amount };
                      }))}
                    >
                      <Typography fontSize="0.9rem" lineHeight={1}>−</Typography>
                    </IconButton>
                    <Typography fontSize="0.78rem" fontWeight={700} sx={{ px: 0.75, color: '#4F46E5' }}>{item.quantity}</Typography>
                    <IconButton
                      size="small"
                      sx={{ borderRadius: 0, width: 24, height: 24, color: '#6366F1' }}
                      onClick={() => setExchangeNewItems((prev) => prev.map((i) => {
                        if (i.product_id !== item.product_id) return i;
                        const quantity = i.quantity + 1;
                        const discount_amount = i.discount_id
                          ? computeDiscountAmount(i.discount_type, i.discount_value, i.modified_value, i.retail_price, quantity)
                          : i.discount_amount;
                        return { ...i, quantity, discount_amount };
                      }))}
                    >
                      <Add sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                  {hasDiscount ? (
                    <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                      <Typography fontSize="0.62rem" color="#94A3B8" sx={{ textDecoration: 'line-through' }}>
                        {fmtAmt(lineSubtotal)}
                      </Typography>
                      <Typography fontSize="0.8rem" fontWeight={700} color="#16A34A">
                        {fmtAmt(lineSubtotal - item.discount_amount)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography fontSize="0.8rem" fontWeight={700} color="#4F46E5" sx={{ minWidth: 52, textAlign: 'right' }}>
                      {fmtAmt(lineSubtotal)}
                    </Typography>
                  )}
                  <IconButton size="small" sx={{ color: '#DC2626', flexShrink: 0 }} onClick={() => setExchangeNewItems((prev) => prev.filter((i) => i.product_id !== item.product_id))}>
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}

        {appliedPromotions.length > 0 && (
          <Box sx={{ mt: 1, bgcolor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 1.5, px: 1.25, py: 0.75 }}>
            {appliedPromotions.map((promo, idx) => (
              <Box key={promo.promotion_id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: idx > 0 ? 0.5 : 0 }}>
                <LocalOffer sx={{ fontSize: 14, color: '#16A34A', mt: 0.2, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#166534', lineHeight: 1.3 }}>
                  {promo.promotion_name} applied — saved {fmtAmt(promo.total_savings)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {nearMissHints.length > 0 && !nearMissDismissed && (
          <Box sx={{ mt: 1, bgcolor: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 1.5, px: 1.25, py: 0.75 }}>
            {nearMissHints.map((hint, idx) => (
              <Box key={hint.promotion_id}>
                {idx > 0 && <Divider sx={{ my: 0.6 }} />}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                  <NotificationsActive sx={{ fontSize: 14, color: '#D97706', mt: 0.2, flexShrink: 0 }} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#92400E', lineHeight: 1.3 }}>{hint.hint_message}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#B45309' }}>{hint.promotion_name}</Typography>
                  </Box>
                  {idx === 0 && (
                    <IconButton size="small" onClick={() => setNearMissDismissed(true)} sx={{ p: 0.25, color: '#D97706', flexShrink: 0 }}>
                      <Close sx={{ fontSize: 13 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <ExchangeItemDiscountPopover
        anchorEl={discountAnchorEl}
        item={discountTargetItem}
        onClose={closeDiscountPopover}
        onApply={handleApplyDiscount}
        onRemove={handleRemoveDiscount}
      />
    </Box>
  );
}
